(function () {
  'use strict';

  if (window.__kfGummyHeroInit__) return;
  window.__kfGummyHeroInit__ = true;

  const SECTION_SELECTOR = '[data-gummy-hero]';
  // .gh-badge is anchored to the heading rather than being a .gh-layer, but it
  // reads the same animation vars, so it is driven from here too.
  const LAYER_SELECTOR = '.gh-layer.is-animated, .gh-badge.is-animated';
  // Max vertical OVERFLOW (in px) a gummy is allowed to render outside its
  // section's top/bottom edges. Internal animation is unconstrained — the cap
  // only kicks in when parallax pushes the gummy past the section bounds
  // (kf-gummy-promo-split uses overflow-y: visible by design).
  const MAX_PARALLAX_Y_PX = 15;

  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(section) {
    if (section.dataset.ghInitialized === '1') return;
    section.dataset.ghInitialized = '1';

    const respectRM = section.dataset.respectReducedMotion !== 'false';
    if (respectRM && prefersReducedMotion()) return;

    const layers = Array.from(section.querySelectorAll(LAYER_SELECTOR))
      .map((el) => {
        const rotate = parseFloat(el.dataset.rotateSpeed) || 0;
        const ySpeed = parseFloat(el.dataset.ySpeed) || 0;
        if (rotate === 0 && ySpeed === 0) return null;
        return {
          el,
          rotate,
          ySpeed,
          lastTy: NaN,
          lastR: NaN,
          tyMin: -Infinity,
          tyMax: Infinity,
        };
      })
      .filter(Boolean);

    if (layers.length === 0) return;

    let inView = false;
    let rafId = 0;
    let lastScrollY = window.scrollY;
    // Sourced from the theme's --header-height CSS variable so we avoid
    // measuring the live #kf-sticky-header — its hide/show transition around
    // scrollY ~ 100 would otherwise bleed into the parallax and produce a
    // visible flick.
    let headerBottomCached = 0;
    const measureHeader = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue(
        '--header-height'
      );
      headerBottomCached = parseFloat(v) || 0;
    };
    measureHeader();

    // Mirrors the section's --gh-unit / --gps-unit CSS token: 1 above 1440 and
    // below 768, linear vw/1440 in between. Applied to translation so parallax
    // travel matches the surrounding layout's responsive scale.
    let unitScale;
    const measureUnit = () => {
      const vw = window.innerWidth;
      unitScale = vw >= 768 && vw < 1440 ? vw / 1440 : 1;
    };
    measureUnit();

    // Precomputes each layer's tyMin/tyMax once per layout so the rAF loop
    // only needs a clamp. Two-pass to keep the forced reflow to one:
    // batched style writes first, then batched reads.
    const measureBounds = () => {
      for (let i = 0; i < layers.length; i++) {
        layers[i].el.style.setProperty('--gh-anim-y', '0px');
        layers[i].el.style.setProperty('--gh-anim-rotate', '0deg');
      }
      const sectionRect = section.getBoundingClientRect();
      const sectionHeight = sectionRect.height;
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const r = layer.el.getBoundingClientRect();
        const staticTop = r.top - sectionRect.top;
        const staticBottom = staticTop + r.height;
        layer.tyMin = -MAX_PARALLAX_Y_PX - staticTop;
        layer.tyMax = MAX_PARALLAX_Y_PX + sectionHeight - staticBottom;
        layer.lastTy = NaN;
        layer.lastR = NaN;
      }
    };
    measureBounds();

    const update = () => {
      rafId = 0;

      const delta = Math.max(
        0,
        headerBottomCached - section.getBoundingClientRect().top
      );

      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const r = delta * layer.rotate * 0.1;
        const rawTy = delta * layer.ySpeed * 0.2 * unitScale;
        const ty =
          layer.tyMin <= layer.tyMax
            ? Math.max(layer.tyMin, Math.min(layer.tyMax, rawTy))
            : 0;
        if (ty !== layer.lastTy) {
          layer.el.style.setProperty('--gh-anim-y', ty.toFixed(2) + 'px');
          layer.lastTy = ty;
        }
        if (r !== layer.lastR) {
          layer.el.style.setProperty('--gh-anim-rotate', r.toFixed(2) + 'deg');
          layer.lastR = r;
        }
      }
    };

    const scheduleUpdate = () => {
      if (!inView || rafId) return;
      rafId = window.requestAnimationFrame(update);
    };

    const onScroll = () => {
      if (window.scrollY === lastScrollY) return;
      lastScrollY = window.scrollY;
      scheduleUpdate();
    };

    const onResize = () => {
      measureUnit();
      measureHeader();
      measureBounds();
      scheduleUpdate();
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          inView = entry.isIntersecting;
          if (inView) scheduleUpdate();
        }
      },
      { rootMargin: '100px 0px' }
    );
    io.observe(section);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    section._ghCleanup = () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafId) window.cancelAnimationFrame(rafId);
      for (const { el } of layers) {
        el.style.removeProperty('--gh-anim-y');
        el.style.removeProperty('--gh-anim-rotate');
      }
      delete section.dataset.ghInitialized;
      delete section._ghCleanup;
    };
  }

  function initAll(root) {
    const scope = root || document;
    const sections = scope.querySelectorAll(SECTION_SELECTOR);
    for (const section of sections) init(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target && target.querySelector) initAll(target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!target || !target.querySelectorAll) return;
    const sections = target.querySelectorAll(SECTION_SELECTOR);
    for (const section of sections) {
      if (typeof section._ghCleanup === 'function') section._ghCleanup();
    }
  });
})();
